import { useEffect, useRef, useSyncExternalStore } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OPS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "../services/apiClient";
import { clearOpsPermissionsCache } from "../services/permissionsApi";
import { updateProfile } from "../store/slices/authSlice";
import { showInfo, showSuccess } from "../utils/toast";

const POLL_MS = 12000;

let snapshot = { revision: 0, events: [] };
const listeners = new Set();

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

function publish(events) {
  snapshot = { revision: snapshot.revision + 1, events: events || [] };
  listeners.forEach((listener) => listener());
}

/** Stable id list of new events whose type matches `pattern`. Empty until one arrives. */
export function useLiveEventTick(pattern) {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return current.events
    .filter((event) => pattern.test(event.event_type || ""))
    .map((event) => event.id)
    .join(",");
}

/**
 * Polls the ops portal for portfolio uploads, role changes, and permission updates.
 * Mount once from the dashboard layout.
 */
export function usePortalPulse() {
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const cursorRef = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (!token || USE_MOCK_AUTH) return undefined;
    let stopped = false;
    cursorRef.current = null;

    async function poll() {
      if (stopped || document.hidden) return;
      try {
        const since = cursorRef.current ?? 0;
        const json = await apiRequest(`${OPS_AUTH.live}?since=${since}`, { method: "GET", token });
        const data = unwrapApiEnvelope(json);
        if (stopped || !data) return;

        const cursor = Number(data.cursor || 0);
        const isFirst = cursorRef.current == null;
        cursorRef.current = cursor;

        if (!isFirst && Array.isArray(data.events) && data.events.length) {
          publish(data.events);
          const notable = data.events.find((event) =>
            /^(portfolio\.uploaded|role\.|team\.)/.test(event.event_type || "")
          );
          if (notable?.title) showInfo(notable.title);
        }

        const role = data.role || "";
        const permissions = Array.isArray(data.permissions) ? data.permissions : [];
        const currentUser = userRef.current;
        const nextKey = [...permissions].sort().join("|");
        const currentKey = [...(currentUser?.permissions || [])].sort().join("|");
        if (role && (role !== currentUser?.role || nextKey !== currentKey)) {
          dispatch(updateProfile({ role, role_slug: role, permissions }));
          clearOpsPermissionsCache();
          showSuccess("Your access was updated.");
        }
      } catch {
        /* The next poll retries. */
      }
    }

    poll();
    const timer = window.setInterval(poll, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) poll();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token, dispatch]);
}

export default function useLiveRefresh() {
  usePortalPulse();
}
